/**
 * SA-1308: where a jump to a message lands in the thread's pane, and the hold
 * that keeps it there while the thread loads. Every jump (Show in thread, a
 * group row's, `?message=`) lands through here.
 *
 * `scrollIntoView({ block: 'center' })` centers the message's midpoint, so an
 * Outlook reply taller than the pane (its HTML carries the whole earlier thread)
 * landed deep in its quoted history. A message that fits is still centered; a
 * taller one lands with its top just below the pane's top.
 *
 * Frames load their HTML one request at a time and grow afterwards, pushing the
 * message off its landing, so the landing is held: re-applied on every content
 * resize until the person's first input over the pane. A wheel, touch or key
 * over an email lands in that frame's own document, never the pane, so the hold
 * listens there too (`allow-same-origin` makes it reachable). A scroll that
 * moves the pane from where the hold last put it ends it as well: WebKit is
 * reported not to run the parent's listeners inside a sandboxed frame without
 * `allow-scripts`, and find in page, Tab or a screen reader scroll with no input
 * the page sees. Chrome's scroll anchoring moves the pane too and so ends the
 * hold, harmlessly: it keeps the message in place on its own.
 */

export const LANDING_MARGIN = 8

/** A message's place in the pane; `top` is its top within the scroll content. */
export interface Landing { top: number; height: number; paneHeight: number; scrollHeight: number }

/** The pane's `scrollTop` that lands the message: centered when it fits with
 * its margins, else its top `LANDING_MARGIN` below the pane's top. */
export function landingTop({ top, height, paneHeight, scrollHeight }: Landing): number {
  const at = height + 2 * LANDING_MARGIN <= paneHeight ? top + height / 2 - paneHeight / 2 : top - LANDING_MARGIN
  return Math.min(Math.max(0, at), Math.max(0, scrollHeight - paneHeight))
}

export function measureLanding(pane: HTMLElement, target: HTMLElement): Landing {
  const rect = target.getBoundingClientRect()
  return {
    top: rect.top - pane.getBoundingClientRect().top + pane.scrollTop,
    height: rect.height,
    paneHeight: pane.clientHeight,
    scrollHeight: pane.scrollHeight,
  }
}

export interface LandingHold {
  readonly active: boolean
  /** Lands the message again on the current geometry; nothing once released. */
  reapply(): void
  release(): void
}

const INPUTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const
const SCROLL_KEYS = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '])
const LISTEN = { capture: true, passive: true } as const

/** Lands the message now and holds it there until the person's first wheel,
 * touch, pointer press or scroll key over the pane or inside an email frame. */
export function holdLanding(pane: HTMLElement, target: () => HTMLElement | null): LandingHold {
  let active = true
  let landed: number | null = null
  const docs = new Set<Document>()

  const onInput = (event: Event) => {
    if (event.type === 'keydown' && !SCROLL_KEYS.has((event as KeyboardEvent).key)) return
    release()
  }
  // Within a pixel of the hold's own landing is the hold's own write.
  const onScroll = () => {
    if (landed !== null && Math.abs(pane.scrollTop - landed) > 1) release()
  }
  const watch = (frame: HTMLIFrameElement) => {
    const doc = frame.contentDocument
    if (!doc || docs.has(doc)) return
    docs.add(doc)
    for (const type of INPUTS) doc.addEventListener(type, onInput, LISTEN)
  }
  // A frame's `load` does not bubble, but it passes the pane in the capture phase.
  const onLoad = (event: Event) => {
    if (event.target instanceof HTMLIFrameElement) watch(event.target)
  }
  const land = () => {
    const el = target()
    if (!el) return
    pane.scrollTop = landingTop(measureLanding(pane, el))
    // Read back: the browser rounds and clamps what it was given.
    landed = pane.scrollTop
  }
  function release() {
    if (!active) return
    active = false
    for (const type of INPUTS) pane.removeEventListener(type, onInput, LISTEN)
    pane.removeEventListener('scroll', onScroll)
    pane.removeEventListener('load', onLoad, true)
    for (const doc of docs) for (const type of INPUTS) doc.removeEventListener(type, onInput, LISTEN)
    docs.clear()
  }

  for (const type of INPUTS) pane.addEventListener(type, onInput, LISTEN)
  pane.addEventListener('scroll', onScroll, { passive: true })
  pane.addEventListener('load', onLoad, true)
  pane.querySelectorAll('iframe').forEach(watch)
  land()
  return {
    get active() { return active },
    reapply() { if (active) land() },
    release,
  }
}
